import React from 'react';

/**
 * Shared action button — colored + optional icon so actions are easy to recognize.
 *
 * Color map (use the same variant for the same kind of action app-wide):
 * - primary  → brand violet (Apply, main CTA)
 * - success  → green (Approve, Accept, Mark Paid, Resolve)
 * - danger   → red (Reject, Delete, destructive)
 * - refresh  → sky blue (Refresh, Recalculate, Reload)
 * - secondary→ slate (Update, Open, neutral actions)
 * - warning  → amber (Unlock, caution)
 * - info     → teal (Copy, info actions)
 */

const VARIANT_CLASS = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 border border-transparent',
  success:
    'bg-emerald-600 text-white hover:bg-emerald-700 border border-transparent',
  danger:
    'bg-red-600 text-white hover:bg-red-700 border border-transparent',
  refresh:
    'bg-sky-600 text-white hover:bg-sky-700 border border-transparent',
  secondary:
    'bg-slate-600 text-white hover:bg-slate-700 border border-transparent',
  warning:
    'bg-amber-600 text-white hover:bg-amber-700 border border-transparent',
  info:
    'bg-teal-600 text-white hover:bg-teal-700 border border-transparent',
};

const SIZE_CLASS = {
  sm: 'rounded-ds-xl px-2.5 py-1.5 text-xs',
  md: 'rounded-ds-xl px-4 py-2 text-xs',
  lg: 'rounded-xl px-4 py-2.5 text-sm',
};

function IconSvg({ children, className = 'h-3.5 w-3.5' }) {
  return (
    <svg
      className={`flex-shrink-0 ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const ICONS = {
  refresh: (
    <IconSvg>
      <path d="M21 12a9 9 0 1 1-2.1-5.7" />
      <path d="M21 3v6h-6" />
    </IconSvg>
  ),
  sync: (
    <IconSvg>
      <path d="M3 12a9 9 0 0 1 15.5-6.4" />
      <path d="M21 3v6h-6" />
      <path d="M21 12a9 9 0 0 1-15.5 6.4" />
      <path d="M3 21v-6h6" />
    </IconSvg>
  ),
  check: (
    <IconSvg>
      <path d="M20 6 9 17l-5-5" />
    </IconSvg>
  ),
  x: (
    <IconSvg>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </IconSvg>
  ),
  pay: (
    <IconSvg>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </IconSvg>
  ),
  copy: (
    <IconSvg>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </IconSvg>
  ),
  eye: (
    <IconSvg>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </IconSvg>
  ),
  unlock: (
    <IconSvg>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 7.2-2.3" />
    </IconSvg>
  ),
  lock: (
    <IconSvg>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </IconSvg>
  ),
  search: (
    <IconSvg>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </IconSvg>
  ),
  resolve: (
    <IconSvg>
      <path d="M12 3v12" />
      <path d="m8 11 4 4 4-4" />
      <path d="M5 21h14" />
    </IconSvg>
  ),
  review: (
    <IconSvg>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
    </IconSvg>
  ),
  undo: (
    <IconSvg>
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6.7 3L3 13" />
    </IconSvg>
  ),
  info: (
    <IconSvg>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </IconSvg>
  ),
};

export default function AppButton({
  variant = 'secondary',
  size = 'md',
  icon,
  children,
  className = '',
  type = 'button',
  ...props
}) {
  const tone = VARIANT_CLASS[variant] || VARIANT_CLASS.secondary;
  const sizing = SIZE_CLASS[size] || SIZE_CLASS.md;
  const iconNode = typeof icon === 'string' ? ICONS[icon] : icon;

  return (
    <button
      type={type}
      className={`inline-flex items-center justify-center gap-1.5 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizing} ${tone} ${className}`}
      {...props}
    >
      {iconNode || null}
      {children != null && children !== false ? <span>{children}</span> : null}
    </button>
  );
}

export { ICONS as APP_BUTTON_ICONS, VARIANT_CLASS as APP_BUTTON_VARIANTS };
